/**
 * lucideCompat (web) — drop-in replacement for `lucide-react`, backed by
 * Phosphor (`@phosphor-icons/react`) with the app's weight policy applied
 * per glyph.
 *
 * Migration mechanic: a Vite alias maps the bare specifier `lucide-react`
 * to this module, so every existing
 *     import { Bell, User } from 'lucide-react';
 * resolves here with the JSX left untouched. The components accept the
 * same props shape (`size`, `color`, `strokeWidth` [ignored], `className`,
 * `style`, `aria-label`) so call sites don't change.
 *
 * Weight policy (mirrors the mobile compat module / Icon.tsx):
 *   • Phosphor **duotone** by default.
 *   • **regular** for the five action families: New (Plus), Download
 *     (Download/Upload), Approve (Check family), Navigate (chevrons +
 *     arrows), Sign out (LogOut).
 *   • Brand **crown** → custom CrownIcon (duotone).
 *
 * Unknown/typo glyph names degrade to a Circle + dev warning rather than
 * crashing — surfaces as a fixable missing icon during QA.
 */
import React from 'react';
import {
  Archive as PhArchive,
  ArrowClockwise as PhArrowClockwise,
  ArrowCounterClockwise as PhArrowCounterClockwise,
  ArrowDown as PhArrowDown,
  ArrowLeft as PhArrowLeft,
  ArrowRight as PhArrowRight,
  ArrowsClockwise as PhArrowsClockwise,
  ArrowsCounterClockwise as PhArrowsCounterClockwise,
  ArrowsDownUp as PhArrowsDownUp,
  ArrowSquareOut as PhArrowSquareOut,
  ArrowUp as PhArrowUp,
  ArrowUUpLeft as PhArrowUUpLeft,
  At as PhAt,
  Backspace as PhBackspace,
  Bell as PhBell,
  BookmarkSimple as PhBookmarkSimple,
  BookOpen as PhBookOpen,
  Briefcase as PhBriefcase,
  CalendarBlank as PhCalendarBlank,
  CalendarDots as PhCalendarDots,
  CalendarPlus as PhCalendarPlus,
  Camera as PhCamera,
  CaretDown as PhCaretDown,
  CaretLeft as PhCaretLeft,
  CaretRight as PhCaretRight,
  CaretUp as PhCaretUp,
  ChartBar as PhChartBar,
  ChatCircle as PhChatCircle,
  Chats as PhChats,
  ChatText as PhChatText,
  Check as PhCheck,
  CheckCircle as PhCheckCircle,
  Checks as PhChecks,
  Circle as PhCircle,
  CircleNotch as PhCircleNotch,
  ClipboardText as PhClipboardText,
  Clock as PhClock,
  Code as PhCode,
  Copy as PhCopy,
  CreditCard as PhCreditCard,
  Crop as PhCrop,
  CurrencyDollar as PhCurrencyDollar,
  DotsSixVertical as PhDotsSixVertical,
  DotsThree as PhDotsThree,
  DownloadSimple as PhDownloadSimple,
  Envelope as PhEnvelope,
  Eye as PhEye,
  EyeSlash as PhEyeSlash,
  File as PhFile,
  FileAudio as PhFileAudio,
  FileImage as PhFileImage,
  Files as PhFiles,
  FileText as PhFileText,
  FileVideo as PhFileVideo,
  FileX as PhFileX,
  FileXls as PhFileXls,
  FileZip as PhFileZip,
  FloppyDisk as PhFloppyDisk,
  Funnel as PhFunnel,
  Gavel as PhGavel,
  Gear as PhGear,
  Gift as PhGift,
  GridFour as PhGridFour,
  HandHeart as PhHandHeart,
  Hash as PhHash,
  Heart as PhHeart,
  House as PhHouse,
  Image as PhImage,
  ImageBroken as PhImageBroken,
  Info as PhInfo,
  InstagramLogo as PhInstagramLogo,
  Key as PhKey,
  Lightning as PhLightning,
  Link as PhLink,
  List as PhList,
  ListChecks as PhListChecks,
  ListNumbers as PhListNumbers,
  Lock as PhLock,
  LockOpen as PhLockOpen,
  MagicWand as PhMagicWand,
  MagnifyingGlass as PhMagnifyingGlass,
  MapPin as PhMapPin,
  Medal as PhMedal,
  Megaphone as PhMegaphone,
  Minus as PhMinus,
  Newspaper as PhNewspaper,
  Paperclip as PhPaperclip,
  PaperPlaneTilt as PhPaperPlaneTilt,
  PencilLine as PhPencilLine,
  PencilSimple as PhPencilSimple,
  Phone as PhPhone,
  Play as PhPlay,
  Plus as PhPlus,
  Power as PhPower,
  Presentation as PhPresentation,
  Printer as PhPrinter,
  Prohibit as PhProhibit,
  Pulse as PhPulse,
  PushPin as PhPushPin,
  PushPinSlash as PhPushPinSlash,
  QrCode as PhQrCode,
  Question as PhQuestion,
  Receipt as PhReceipt,
  Scissors as PhScissors,
  Scroll as PhScroll,
  ShareNetwork as PhShareNetwork,
  Shield as PhShield,
  ShieldCheck as PhShieldCheck,
  ShieldSlash as PhShieldSlash,
  ShieldWarning as PhShieldWarning,
  Shuffle as PhShuffle,
  SignOut as PhSignOut,
  Smiley as PhSmiley,
  PauseCircle as PhPauseCircle,
  Sparkle as PhSparkle,
  Square as PhSquare,
  SquaresFour as PhSquaresFour,
  Star as PhStar,
  Tag as PhTag,
  TextB as PhTextB,
  TextItalic as PhTextItalic,
  TextStrikethrough as PhTextStrikethrough,
  TextUnderline as PhTextUnderline,
  ToggleLeft as PhToggleLeft,
  ToggleRight as PhToggleRight,
  Tray as PhTray,
  Trash as PhTrash,
  TrendUp as PhTrendUp,
  Trophy as PhTrophy,
  TShirt as PhTShirt,
  UploadSimple as PhUploadSimple,
  User as PhUser,
  UserPlus as PhUserPlus,
  UserMinus as PhUserMinus,
  Users as PhUsers,
  Wallet as PhWallet,
  Warning as PhWarning,
  WarningCircle as PhWarningCircle,
  Waves as PhWaves,
  WifiSlash as PhWifiSlash,
  X as PhX,
  XCircle as PhXCircle,
} from '@phosphor-icons/react';
import type { Icon } from '@phosphor-icons/react';
import { CrownIcon } from './CrownIcon';

type Weight = 'regular' | 'duotone';

interface CompatProps {
  size?: number;
  color?: string;
  /** Accepted for lucide API compatibility; Phosphor uses `weight`. */
  strokeWidth?: number;
  className?: string;
  style?: React.CSSProperties;
  'aria-label'?: string;
}

type PhGlyph = Icon;

/**
 * Static name→component map. Only the glyphs referenced below are imported
 * (see the named import above), so the bundler can tree-shake the rest of
 * `@phosphor-icons/react` out of the production build.
 */
const GLYPHS: Record<string, PhGlyph> = {
  Archive: PhArchive,
  ArrowClockwise: PhArrowClockwise,
  ArrowCounterClockwise: PhArrowCounterClockwise,
  ArrowDown: PhArrowDown,
  ArrowLeft: PhArrowLeft,
  ArrowRight: PhArrowRight,
  ArrowsClockwise: PhArrowsClockwise,
  ArrowsCounterClockwise: PhArrowsCounterClockwise,
  ArrowsDownUp: PhArrowsDownUp,
  ArrowSquareOut: PhArrowSquareOut,
  ArrowUp: PhArrowUp,
  ArrowUUpLeft: PhArrowUUpLeft,
  At: PhAt,
  Backspace: PhBackspace,
  Bell: PhBell,
  BookmarkSimple: PhBookmarkSimple,
  BookOpen: PhBookOpen,
  Briefcase: PhBriefcase,
  CalendarBlank: PhCalendarBlank,
  CalendarDots: PhCalendarDots,
  Camera: PhCamera,
  CaretDown: PhCaretDown,
  CaretLeft: PhCaretLeft,
  CaretRight: PhCaretRight,
  CaretUp: PhCaretUp,
  ChartBar: PhChartBar,
  ChatCircle: PhChatCircle,
  Chats: PhChats,
  ChatText: PhChatText,
  Check: PhCheck,
  CheckCircle: PhCheckCircle,
  Checks: PhChecks,
  Circle: PhCircle,
  CircleNotch: PhCircleNotch,
  ClipboardText: PhClipboardText,
  Clock: PhClock,
  Code: PhCode,
  Copy: PhCopy,
  CreditCard: PhCreditCard,
  Crop: PhCrop,
  CurrencyDollar: PhCurrencyDollar,
  DotsSixVertical: PhDotsSixVertical,
  DotsThree: PhDotsThree,
  DownloadSimple: PhDownloadSimple,
  Envelope: PhEnvelope,
  Eye: PhEye,
  EyeSlash: PhEyeSlash,
  File: PhFile,
  FileAudio: PhFileAudio,
  FileImage: PhFileImage,
  Files: PhFiles,
  FileText: PhFileText,
  FileVideo: PhFileVideo,
  FileX: PhFileX,
  FileXls: PhFileXls,
  FileZip: PhFileZip,
  FloppyDisk: PhFloppyDisk,
  Funnel: PhFunnel,
  Gavel: PhGavel,
  Gear: PhGear,
  Gift: PhGift,
  GridFour: PhGridFour,
  HandHeart: PhHandHeart,
  Hash: PhHash,
  Heart: PhHeart,
  House: PhHouse,
  Image: PhImage,
  ImageBroken: PhImageBroken,
  Info: PhInfo,
  InstagramLogo: PhInstagramLogo,
  Key: PhKey,
  Lightning: PhLightning,
  Link: PhLink,
  List: PhList,
  ListChecks: PhListChecks,
  ListNumbers: PhListNumbers,
  Lock: PhLock,
  LockOpen: PhLockOpen,
  MagicWand: PhMagicWand,
  MagnifyingGlass: PhMagnifyingGlass,
  MapPin: PhMapPin,
  Medal: PhMedal,
  Megaphone: PhMegaphone,
  Minus: PhMinus,
  Newspaper: PhNewspaper,
  Paperclip: PhPaperclip,
  PaperPlaneTilt: PhPaperPlaneTilt,
  PencilLine: PhPencilLine,
  PencilSimple: PhPencilSimple,
  Phone: PhPhone,
  Play: PhPlay,
  CalendarPlus: PhCalendarPlus,
  Power: PhPower,
  Plus: PhPlus,
  Presentation: PhPresentation,
  Printer: PhPrinter,
  Prohibit: PhProhibit,
  Pulse: PhPulse,
  PushPin: PhPushPin,
  PushPinSlash: PhPushPinSlash,
  QrCode: PhQrCode,
  Question: PhQuestion,
  Receipt: PhReceipt,
  Scissors: PhScissors,
  Scroll: PhScroll,
  ShareNetwork: PhShareNetwork,
  Shield: PhShield,
  ShieldCheck: PhShieldCheck,
  ShieldSlash: PhShieldSlash,
  ShieldWarning: PhShieldWarning,
  Shuffle: PhShuffle,
  SignOut: PhSignOut,
  Smiley: PhSmiley,
  PauseCircle: PhPauseCircle,
  Sparkle: PhSparkle,
  Square: PhSquare,
  SquaresFour: PhSquaresFour,
  Star: PhStar,
  Tag: PhTag,
  TextB: PhTextB,
  TextItalic: PhTextItalic,
  TextStrikethrough: PhTextStrikethrough,
  TextUnderline: PhTextUnderline,
  ToggleLeft: PhToggleLeft,
  ToggleRight: PhToggleRight,
  Tray: PhTray,
  Trash: PhTrash,
  TrendUp: PhTrendUp,
  Trophy: PhTrophy,
  TShirt: PhTShirt,
  UploadSimple: PhUploadSimple,
  User: PhUser,
  UserPlus: PhUserPlus,
  UserMinus: PhUserMinus,
  Users: PhUsers,
  Wallet: PhWallet,
  Warning: PhWarning,
  WarningCircle: PhWarningCircle,
  Waves: PhWaves,
  WifiSlash: PhWifiSlash,
  X: PhX,
  XCircle: PhXCircle,
};

function make(glyphName: string, weight: Weight) {
  const Comp = (props: CompatProps) => {
    const {
      size = 24,
      color = 'currentColor',
      className,
      style,
      'aria-label': ariaLabel,
    } = props;
    const found = GLYPHS[glyphName];
    const Glyph = found ?? PhCircle;
    if (import.meta.env.DEV && !found) {
      // eslint-disable-next-line no-console
      console.warn(`[lucideCompat] Missing Phosphor glyph "${glyphName}" — using fallback.`);
    }
    if (!Glyph) return null;
    return (
      <Glyph
        size={size}
        color={color}
        weight={weight}
        className={className}
        style={style}
        aria-label={ariaLabel}
      />
    );
  };
  Comp.displayName = `Compat(${glyphName})`;
  return Comp;
}

const D: Weight = 'duotone';
const R: Weight = 'regular';

/** Shared lucide-compatible component type. */
export type LucideIcon = React.ComponentType<{
  size?: number;
  color?: string;
  className?: string;
  strokeWidth?: number;
}>;

// ── Brand crown → custom CrownIcon (duotone shaded) ──────────────────
interface CrownCompatProps {
  size?: number;
  color?: string;
  className?: string;
}
const CrownCompat = (props: CrownCompatProps) => (
  <CrownIcon
    size={props.size ?? 24}
    color={props.color ?? 'currentColor'}
    className={props.className}
    weight="duotone"
  />
);
CrownCompat.displayName = 'Compat(Crown)';
export const Crown = CrownCompat;
export { CrownCompat as CrownIcon };

// ── Action glyphs → regular weight (the policy exceptions) ───────────
// New
export const Plus = make('Plus', R);
// Download / Upload
export const Download = make('DownloadSimple', R);
export const Upload = make('UploadSimple', R);
export const UploadCloud = make('UploadSimple', R);
// Approve (the Check family)
export const Check = make('Check', R);
export const CheckCheck = make('Checks', R);
export const CheckCircle = make('CheckCircle', R);
export const CheckCircle2 = make('CheckCircle', R);
export const CheckIcon = make('Check', R);
// Navigate (chevrons + arrows)
export const ChevronRight = make('CaretRight', R);
export const ChevronRightIcon = make('CaretRight', R);
export const ChevronLeft = make('CaretLeft', R);
export const ChevronUp = make('CaretUp', R);
export const ChevronUpIcon = make('CaretUp', R);
export const ChevronDown = make('CaretDown', R);
export const ChevronDownIcon = make('CaretDown', R);
export const ArrowRight = make('ArrowRight', R);
export const ArrowLeft = make('ArrowLeft', R);
export const ArrowUp = make('ArrowUp', R);
export const ArrowDown = make('ArrowDown', R);
export const ArrowUpDown = make('ArrowsDownUp', R);
// Sort indicator (lucide's up/down chevron) → Phosphor up/down arrows
export const ChevronsUpDown = make('ArrowsDownUp', R);
// Sign out
export const LogOut = make('SignOut', R);

// ── Everything else → duotone ────────────────────────────────────────
export const Activity = make('Pulse', D);
export const AlertCircle = make('WarningCircle', D);
export const AlertTriangle = make('Warning', D);
export const AtSign = make('At', D);
export const Award = make('Medal', D);
export const Archive = make('Archive', D);
// Prohibit / revoke (lucide `Ban` — a prohibition circle-with-slash).
export const Ban = make('Prohibit', D);
export const BarChart3 = make('ChartBar', D);
export const Bell = make('Bell', D);
export const Bold = make('TextB', D);
export const BookOpen = make('BookOpen', D);
export const Bookmark = make('BookmarkSimple', D);
export const BookmarkX = make('BookmarkSimple', D);
export const Briefcase = make('Briefcase', D);
export const Calendar = make('CalendarBlank', D);
export const CalendarClock = make('CalendarBlank', D);
export const CalendarDays = make('CalendarDots', D);
export const CalendarPlus = make('CalendarPlus', D);
export const Camera = make('Camera', D);
export const Circle = make('Circle', D);
export const CircleIcon = make('Circle', D);
export const ClipboardCheck = make('ClipboardText', D);
export const ClipboardList = make('ClipboardText', D);
export const Clock = make('Clock', D);
export const Code = make('Code', D);
export const Copy = make('Copy', D);
export const CreditCard = make('CreditCard', D);
export const Crop = make('Crop', D);
export const Delete = make('Backspace', D);
export const DollarSign = make('CurrencyDollar', D);
export const Edit2 = make('PencilSimple', D);
export const ExternalLink = make('ArrowSquareOut', D);
export const Eye = make('Eye', D);
export const EyeOff = make('EyeSlash', D);
export const File = make('File', D);
export const FileArchive = make('FileZip', D);
export const FileAudio = make('FileAudio', D);
export const FileCheck = make('FileText', D);
export const FileImage = make('FileImage', D);
export const FileSpreadsheet = make('FileXls', D);
export const FileText = make('FileText', D);
export const FileX = make('FileX', D);
export const FileVideo = make('FileVideo', D);
export const Files = make('Files', D);
export const Filter = make('Funnel', D);
export const Gift = make('Gift', D);
export const Gavel = make('Gavel', D);
export const Grid3X3 = make('GridFour', D);
export const GripVertical = make('DotsSixVertical', R);
export const Hash = make('Hash', D);
export const Heart = make('Heart', D);
export const HeartHandshake = make('HandHeart', D);
export const HelpCircle = make('Question', D);
export const Home = make('House', D);
export const Image = make('Image', D);
export const ImageOff = make('ImageBroken', D);
export const Inbox = make('Tray', D);
export const Info = make('Info', D);
export const Instagram = make('InstagramLogo', D);
export const Italic = make('TextItalic', D);
export const Key = make('Key', D);
export const KeyRound = make('Key', D);
export const LayoutDashboard = make('SquaresFour', D);
export const Link2 = make('Link', D);
export const List = make('List', D);
export const ListChecks = make('ListChecks', D);
export const ListOrdered = make('ListNumbers', D);
export const Loader2 = make('CircleNotch', D);
export const Lock = make('Lock', D);
export const Unlock = make('LockOpen', D);
export const Mail = make('Envelope', D);
export const MapPin = make('MapPin', D);
export const Megaphone = make('Megaphone', D);
export const Menu = make('List', D);
export const Minus = make('Minus', R);
export const MessageCircle = make('ChatCircle', D);
export const MessageSquare = make('ChatText', D);
export const MessagesSquare = make('Chats', D);
export const MoreHorizontal = make('DotsThree', D);
export const Newspaper = make('Newspaper', D);
export const Paperclip = make('Paperclip', D);
export const PenLine = make('PencilLine', D);
export const Pencil = make('PencilSimple', D);
export const Power = make('Power', D);
export const Phone = make('Phone', D);
export const Pin = make('PushPin', D);
export const PinOff = make('PushPinSlash', D);
export const Play = make('Play', R);
export const Presentation = make('Presentation', D);
export const Printer = make('Printer', D);
export const QrCode = make('QrCode', D);
export const Receipt = make('Receipt', D);
export const RefreshCcw = make('ArrowsCounterClockwise', D);
export const RefreshCw = make('ArrowsClockwise', D);
export const RotateCcw = make('ArrowCounterClockwise', R);
export const RotateCw = make('ArrowClockwise', D);
export const Save = make('FloppyDisk', D);
export const Scissors = make('Scissors', D);
export const ScrollText = make('Scroll', D);
export const Search = make('MagnifyingGlass', D);
export const Send = make('PaperPlaneTilt', D);
export const Settings = make('Gear', D);
export const Share2 = make('ShareNetwork', D);
export const Shield = make('Shield', D);
export const ShieldAlert = make('ShieldWarning', D);
export const ShieldCheck = make('ShieldCheck', D);
export const ShieldOff = make('ShieldSlash', D);
export const Shirt = make('TShirt', D);
export const Shuffle = make('Shuffle', D);
export const Smile = make('Smiley', D);
// tenant-foundation Task 18: the paused-organization state. Duotone per the
// default weight policy (it is a status glyph, not one of the five action families).
export const PauseCircle = make('PauseCircle', D);
export const Sparkles = make('Sparkle', D);
export const Square = make('Square', R);
export const Star = make('Star', D);
export const Strikethrough = make('TextStrikethrough', D);
export const Tag = make('Tag', D);
export const ToggleLeft = make('ToggleLeft', D);
export const ToggleRight = make('ToggleRight', D);
export const Trash2 = make('Trash', D);
export const TrendingUp = make('TrendUp', D);
export const Trophy = make('Trophy', D);
export const Underline = make('TextUnderline', D);
export const Undo2 = make('ArrowUUpLeft', D);
export const User = make('User', D);
export const UserPlus = make('UserPlus', D);
export const UserX = make('UserMinus', D);
export const Users = make('Users', D);
export const Wallet = make('Wallet', D);
export const Wand2 = make('MagicWand', D);
export const Waves = make('Waves', D);
export const WifiOff = make('WifiSlash', D);
export const X = make('X', D);
export const XCircle = make('XCircle', D);
export const XIcon = make('X', D);
export const Zap = make('Lightning', D);
